/**
 * SuggestionsSettingsModal - Modal for configuring clinical suggestions settings.
 *
 * Allows users to:
 * - Toggle clinical suggestions on/off
 * - Filter which categories of suggestions appear
 *
 * This modal version is used when the SuggestionsButton popover isn't accessible
 * (e.g., when opened from the expanded peek sheet).
 */

import {
  ALL_CATEGORIES,
  categoryConfig,
  getCategoryIcon,
  type SuggestedChipCategory,
} from '@/components/ui/transcript-chips/types';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { Switch } from '@heidi/design-system/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { useTranslations } from '@heidi/runtime/i18n';
import { X } from 'lucide-react';
import { memo } from 'react';

export interface SuggestionsSettingsModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Whether suggestions are enabled */
  suggestEnabled?: boolean;
  /** Callback when suggestions toggle changes */
  onSuggestEnabledChange?: (enabled: boolean) => void;
  /** Enabled suggestion categories */
  enabledCategories?: Set<SuggestedChipCategory>;
  /** Callback when a category filter is toggled */
  onCategoryToggle?: (category: SuggestedChipCategory) => void;
}

export const SuggestionsSettingsModal = memo(
  ({
    open,
    onOpenChange,
    suggestEnabled = true,
    onSuggestEnabledChange,
    enabledCategories = new Set(),
    onCategoryToggle,
  }: SuggestionsSettingsModalProps) => {
    const { t } = useTranslations();

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('settings.evidence.suggestionsSettingsTitle')}</DialogTitle>
            <DialogDescription>
              {t('settings.evidence.suggestionsSettingsDescription')}
            </DialogDescription>
          </DialogHeader>

          <DialogClose asChild>
            <Button
              variant="ghost"
              size="xs"
              iconOnly
              className="absolute end-3 top-3 size-7 text-text-tertiary hover:text-text-secondary"
              aria-label={t('common.close')}
            >
              <X size={16} />
            </Button>
          </DialogClose>

          <div className="space-y-6 py-2">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-text-primary">
                  {t('settings.evidence.clinicalConsiderations')}
                </p>
                <p className="text-xs text-text-secondary">
                  {t('settings.evidence.enableSuggestionsDescription')}
                </p>
              </div>
              <Switch
                checked={suggestEnabled}
                onCheckedChange={onSuggestEnabledChange}
                aria-label={t('settings.evidence.toggleClinicalConsiderations')}
              />
            </div>

            {suggestEnabled && (
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    {t('settings.evidence.categories')}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {t('settings.evidence.categoriesFilterDescription')}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {ALL_CATEGORIES.map((category) => {
                    const config = categoryConfig[category];
                    const CategoryIcon = getCategoryIcon(category);
                    const isEnabled = enabledCategories.has(category);

                    return (
                      <Tooltip key={category}>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onCategoryToggle?.(category)}
                            className={cn(
                              'transition-all',
                              isEnabled
                                ? config.isDestructive
                                  ? 'border border-validation-destructive-border bg-validation-destructive-muted text-validation-destructive hover:bg-validation-destructive-hover'
                                  : 'border border-tier-pro/30 bg-tier-pro/10 text-tier-pro-text hover:bg-tier-pro/20'
                                : 'text-text-tertiary hover:text-text-secondary'
                            )}
                          >
                            <CategoryIcon size={14} />
                            {t(`settings.evidence.category.${category}.label`)}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {t(`settings.evidence.category.${category}.description`)}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);

SuggestionsSettingsModal.displayName = 'SuggestionsSettingsModal';

export default SuggestionsSettingsModal;
